// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { useRef, useEffect, useMemo } from 'react';
import { useAvatarEmotion } from '../../hooks/useAvatarEmotion';
import { useGLTF, useAnimations } from '@react-three/drei';
import { useAvatarLipsync } from '../../hooks/useAvatarLipsync';
import { useSimulationContext } from '../../providers/SimulationProvider';
import {
  AVATURN_CLIP_NAME,
  getAvaturnBasePose,
  stripMorphTracksFromClips
} from '../../helpers/animation';
import { useAvaturnCustomAnimation } from '../../hooks/multi-character/useAvaturnCustomAnimation';
import { EMOTION_MESH_NAME } from '../../constants/emotionBlendshapes';
import * as THREE from 'three';
import characterConfigs from '../../config/characterConfig';

export function AvatarLoader({
  client,
  characterId,
  enableCustomAnimation = false
}) {
  const { characterGlb, setIsSceneReady } = useSimulationContext();
  const group = useRef();
  const isReadyFired = useRef(false);

  const remoteUrl = characterGlb?.file_url ?? null;
  const avatarId = characterId?.split('_')[0] || '500';
  const config = characterConfigs[avatarId] || characterConfigs['500'];
  const modelPath = remoteUrl ?? config.model;

  const {
    nodes,
    materials,
    animations: rawAnimations,
    scene
  } = useGLTF(modelPath);
  const morphDictionaries = useMemo(
    () => ({
      [EMOTION_MESH_NAME.head]:
        nodes?.[EMOTION_MESH_NAME.head]?.morphTargetDictionary,
      [EMOTION_MESH_NAME.teeth]:
        nodes?.[EMOTION_MESH_NAME.teeth]?.morphTargetDictionary
    }),
    [nodes]
  );
  const animations = useMemo(
    () => stripMorphTracksFromClips(rawAnimations, morphDictionaries),
    [rawAnimations, morphDictionaries]
  );
  const { actions } = useAnimations(animations, group);

  // Multi-character scenes swap the looping clip for a custom animation. That
  // needs the clip's first keyframe as a base pose, because the GLB rest pose is
  // a T-pose — so with nothing to pose from (a remote GLB carrying no clip) the
  // custom animation stays off and the clip plays, as it always has.
  const basePose = useMemo(
    () => getAvaturnBasePose(rawAnimations),
    [rawAnimations]
  );
  const isCustomAnimationActive = enableCustomAnimation && !!basePose;

  const avatarTransform = useMemo(() => {
    if (characterGlb) {
      return {
        position: characterGlb.position,
        rotation: characterGlb.rotation
      };
    }

    return {
      position: config.position || [0.07, -0.1, 0],
      rotation: config.rotation || [0, 0, 0]
    };
  }, [characterGlb, config.position, config.rotation]);

  // Update state when the scene is loaded
  useEffect(() => {
    if (isReadyFired.current || !nodes || !nodes.Hips || !materials) return;

    const rafId = requestAnimationFrame(() => {
      isReadyFired.current = true;
      setIsSceneReady(true);
    });

    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, materials]);

  // Play the avatar animation
  useEffect(() => {
    // The custom animation owns the rig instead.
    if (isCustomAnimationActive) return;

    if (actions && actions[AVATURN_CLIP_NAME]) {
      const action = actions[AVATURN_CLIP_NAME];

      // Configure the animation to loop smoothly
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.clampWhenFinished = false;
      action.crossFadeFrom(action, 0, false);

      // Start the animation
      action.play();

      // Cleanup function to stop animation when component unmounts
      return () => {
        if (action) {
          action.stop();
        }
      };
    }
  }, [actions, isCustomAnimationActive]);

  // Takes the rendered group, not `scene`: <primitive object={nodes.Hips} />
  // reparents the skeleton out of the GLTF scene, so only the group reaches both
  // the bones and the meshes whose influence arrays the face writers share.
  useAvaturnCustomAnimation({
    renderRoot: group,
    basePose,
    seedKey: `${characterId ?? ''}|${modelPath}`,
    enabled: isCustomAnimationActive
  });

  // Lipsync drives the mouth; emotion overlay handles upper face (and mouth when idle).
  useAvatarLipsync({ client, scene });
  useAvatarEmotion({ client, scene });

  // Render the character meshes
  const renderCharacterMeshes = () => {
    if (!nodes || !materials) return null;

    const meshes = [];

    // Get all skinned meshes from the model
    const allMeshes = Object.keys(nodes).filter(
      (key) => nodes[key]?.isSkinnedMesh || nodes[key]?.type === 'SkinnedMesh'
    );

    allMeshes.forEach((meshName) => {
      const node = nodes[meshName];
      if (!node) return;

      let material;

      // Material mapping logic based on mesh name
      if (meshName === 'Body_Mesh') {
        material = materials.Body;
      } else if (meshName === 'Eye_Mesh') {
        material = materials.Eyes;
      } else if (meshName === 'EyeAO_Mesh') {
        material = materials.EyeAO;
      } else if (meshName === 'Eyelash_Mesh') {
        material = materials.Eyelash;
      } else if (meshName === 'Head_Mesh') {
        material = materials.Head;
      } else if (meshName === 'Teeth_Mesh') {
        material = materials.Teeth;
      } else if (meshName === 'Tongue_Mesh') {
        material = materials.Teeth || materials.Tounge; // Handle typo in Arjun model
      } else if (meshName.startsWith('avaturn_')) {
        // Dynamic avatar parts (hair, clothes, accessories)
        const materialName = `${meshName}_material`;
        material = materials[materialName];
      } else {
        // Fallback to basic material mapping
        material = materials[node.material?.name] || materials.Body;
      }

      if (material && node.geometry) {
        meshes.push(
          <skinnedMesh
            key={meshName}
            name={meshName}
            geometry={node.geometry}
            material={material}
            skeleton={node.skeleton}
            morphTargetDictionary={node.morphTargetDictionary}
            morphTargetInfluences={node.morphTargetInfluences}
          />
        );
      }
    });

    return meshes;
  };

  // Check if nodes are available
  if (!nodes || !nodes.Hips) {
    return null;
  }

  return (
    <group
      ref={group}
      {...client}
      position={avatarTransform.position}
      rotation={avatarTransform.rotation}
      dispose={null}
    >
      <group name='Scene'>
        <group name='Armature'>
          <primitive object={nodes.Hips} />
          {renderCharacterMeshes()}
        </group>
      </group>
    </group>
  );
}
